import React, { useMemo } from 'react';
import i18n from '../../../../../i18n';
import { METRICS, fmt } from '../../../data/metrics';
import type { DrillBasis, DrillPanel, OccupancyRhythm, RegimeDay } from '../../../types';
import { OccupancyDayChart, clockHourTicks, rhythmSeatMaxY } from '../DrillPanels';
import { DrillTile } from './DrillTile';
import '../../../styles/drill-capacity.css';

function occupancyChartMaxY(values: number[]): number {
  const peak = Math.max(...values, 1);
  const padded = Math.ceil((peak * 1.3) / 5) * 5;
  return Math.min(100, Math.max(15, padded));
}

/** The typical day as a rhythm day the chart can draw in seats. */
export function typicalRhythmDay(rhythm: OccupancyRhythm): RegimeDay {
  const series = rhythm.seats.map((v, i) => {
    const cap = rhythm.capacity[i] ?? 0;
    return cap > 0 ? Math.min(100, Math.round((v / cap) * 100)) : null;
  });
  return {
    dow: 0,
    label: '',
    capacity: Math.max(0, ...rhythm.capacity),
    operatingDays: rhythm.n,
    peakPct: 0,
    peakLabel: '',
    avgPct: 0,
    openLabel: '',
    series,
    shiftBands: [],
    seatsSeries: rhythm.seats,
    capacitySeries: rhythm.capacity,
  };
}

export function DrillRegimeSection({
  panel,
  basis,
  rhythm,
}: {
  metricKey: string;
  panel: Extract<DrillPanel, { kind: 'regimeGrid' }>;
  /** Guests behind the drill (the tile's n). */
  basis?: DrillBasis;
  /** Typical day with empty seats at each shift's peak (single venue). */
  rhythm?: OccupancyRhythm | null;
}) {
  const activeDays = useMemo(
    () => panel.days.filter(d => d.operatingDays > 0),
    [panel.days],
  );
  const seatMode = activeDays.some(d => d.capacitySeries?.length);
  const chartMaxY = useMemo(() => {
    if (seatMode) return rhythmSeatMaxY(activeDays);
    const allValues = activeDays.flatMap(d =>
      d.series.filter((v): v is number => v != null),
    );
    return occupancyChartMaxY(allValues.length ? allValues : [0]);
  }, [activeDays, seatMode]);
  const hourTicks = useMemo(
    () => clockHourTicks(panel.axisStart, panel.axisEnd),
    [panel.axisStart, panel.axisEnd],
  );

  const shiftLines = useMemo(() => rhythm?.shifts?.length ? (
    <ul className="dcap-shift-peaks">
      {rhythm.shifts.map(s => (
        <li key={s.label}>
          <span className="dcap-shift-name">{s.label}</span>
          <span className="dcap-shift-val">
            {i18n.t('performance:drillUi.rhythmEmptyAtPeak', {
              empty: fmt.num(s.emptySeats),
              capacity: fmt.num(s.capacity),
              time: s.peakLabel,
            })}
          </span>
          <span className="dcap-n">
            {i18n.t('performance:drillUi.rhythmShiftN', { n: fmt.num(s.n) })}
          </span>
        </li>
      ))}
    </ul>
  ) : null, [rhythm]);

  const inspector = useMemo(
    () => (
      <div className="dcrux-regime-days">
        {shiftLines}
        {activeDays.map(d => (
          <div className="regime-rhythm-day" key={d.dow}>
            <div className="regime-rhythm-head">
              <span className="regime-rhythm-dow">{d.label}</span>
              <span className="regime-rhythm-avg">
                <b>{d.avgPct}%</b>
                <span className="regime-rhythm-avg-label">{i18n.t('performance:drillUi.busyAvg')}</span>
              </span>
              <span className="regime-rhythm-meta">
                {i18n.t('performance:drillUi.peakAt', { pct: d.peakPct, label: d.peakLabel })}
                {' · '}
                {i18n.t('performance:drillUi.rhythmShiftN', { n: fmt.num(d.operatingDays) })}
              </span>
            </div>
            <OccupancyDayChart
              day={d}
              maxY={chartMaxY}
              axisStart={panel.axisStart}
              axisEnd={panel.axisEnd}
              slotMinutes={panel.slotMinutes}
              hourTicks={hourTicks}
            />
          </div>
        ))}
      </div>
    ),
    [activeDays, chartMaxY, panel.axisStart, panel.axisEnd, panel.slotMinutes, hourTicks, shiftLines],
  );

  const typical = useMemo(() => (rhythm ? typicalRhythmDay(rhythm) : null), [rhythm]);
  if (!activeDays.length) return null;

  const peakDay = [...activeDays].sort((a, b) => b.peakPct - a.peakPct)[0];
  // The tile draws the typical day (every day with guests); without it, the
  // busiest weekday.
  const tileDay = typical ?? peakDay;
  const tileAxisStart = rhythm ? rhythm.axisStart : panel.axisStart;
  const tileAxisEnd = rhythm ? rhythm.axisEnd : panel.axisEnd;
  const tileMaxY = typical ? rhythmSeatMaxY([typical]) : chartMaxY;

  return (
    <DrillTile
      sectionId="regime"
      span={rhythm ? 6 : 4}
      title={panel.title}
      meta={
        rhythm
          ? `${i18n.t('performance:drillUi.rhythmTypicalDay')} · ${i18n.t('performance:drillUi.rhythmShiftN', { n: fmt.num(rhythm.n) })}`
          : i18n.t('performance:drillUi.slotFillVs', {
              minutes: panel.slotMinutes,
              basis: panel.basisLabel.toLowerCase(),
            })
      }
      inspector={inspector}
      n={
        basis
          ? i18n.t('performance:drillUi.nOperatingDays', {
              guests: fmt.num(basis.guests),
              days: fmt.num(activeDays.reduce((a, d) => a + d.operatingDays, 0)),
            })
          : undefined
      }
    >
      <div className="dcap-rhythm-tile">
        <OccupancyDayChart
          day={tileDay}
          maxY={tileMaxY}
          axisStart={tileAxisStart}
          axisEnd={tileAxisEnd}
          slotMinutes={rhythm?.slotMinutes ?? panel.slotMinutes}
          hourTicks={clockHourTicks(tileAxisStart, tileAxisEnd)}
        />
        {typical ? (
          <div className="dcap-legend" aria-hidden>
            <span className="dcap-legend-seats">{i18n.t('performance:drillUi.rhythmSeatsTaken')}</span>
            <span className="dcap-legend-cap">{i18n.t('performance:drillUi.rhythmSeatsOnOffer')}</span>
          </div>
        ) : null}
        {shiftLines}
      </div>
    </DrillTile>
  );
}
